import type { ServerDeploymentInfo } from '@/api/type-server'

export type ScalingPolicyValues = {
  scalingMode: 0 | 1
  maxReplicas: number
  concurrencyPerReplica?: string | number | null
  maxPendingRequests?: string | number | null
}

type SavedPolicy = Pick<
  ServerDeploymentInfo,
  'scalingMode' | 'maxReplicas' | 'concurrencyPerReplica' | 'maxPendingRequests'
>

export function parseRequestCount(value: unknown, minimum: number): number | undefined {
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value)))
    return undefined
  const number = Number(value)
  return Number.isSafeInteger(number) && number >= minimum ? number : undefined
}

export function getScalingPolicyValues(saved?: SavedPolicy): ScalingPolicyValues {
  const hasAutoSettings = saved?.scalingMode === 1 || (saved?.concurrencyPerReplica ?? 0) > 0
  return {
    scalingMode: saved?.scalingMode ?? 0,
    maxReplicas: saved?.maxReplicas ?? 1,
    concurrencyPerReplica: hasAutoSettings ? saved?.concurrencyPerReplica : undefined,
    maxPendingRequests: hasAutoSettings ? saved?.maxPendingRequests : undefined,
  }
}

export function isValidMaxReplicas(value: number, savedMaximum?: number) {
  // Existing API values above the UI limit must survive an unrelated edit.
  return Number.isSafeInteger(value) && value >= 1 && (value <= 10 || value === savedMaximum)
}

export function getScalingPolicyPayload(values: ScalingPolicyValues, saved?: SavedPolicy) {
  const concurrency = parseRequestCount(values.concurrencyPerReplica, 1)
  const pending = parseRequestCount(values.maxPendingRequests, 0)
  if (
    ![0, 1].includes(values.scalingMode) ||
    !isValidMaxReplicas(values.maxReplicas, saved?.maxReplicas) ||
    (values.scalingMode === 1 && (concurrency === undefined || pending === undefined))
  ) {
    throw new Error('Invalid scaling policy')
  }
  return {
    scaling_mode: values.scalingMode,
    max_replicas: values.maxReplicas,
    concurrency_per_replica: concurrency ?? saved?.concurrencyPerReplica ?? 0,
    max_pending_requests: pending ?? saved?.maxPendingRequests ?? 0,
  }
}
